import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ChartData, ChartDataset, ChartOptions, Chart, registerables } from 'chart.js';
import { ChartUtils } from '../../utils';

Chart.register(...registerables);

@Component({
    selector: 'ca-line-chart',
    templateUrl: './line-chart.component.html',
    styleUrls: ['./line-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LineChartComponent {
    labels = input.required<string[]>();
    tooltipValueLabel = input.required<string>();
    tooltipCategoryLabel = input.required<string>();
    datasets = input.required<ChartDataset<'line'>[]>();
    locale = input.required<string>();
    valueUnit = input<string | null>('€');
    indexAxis = input<'y' | 'x' | undefined>('x');
    ticksCallback = input<(value: string | number) => string>();

    lineChartData = computed<ChartData<'line'>>(() => ({
        labels: this.labels(),
        datasets: this.datasets()!
    }));

    lineChartOptions = computed<ChartOptions<'line'>>(() => {
        return {
            indexAxis: 'x',
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: 'category',
                    grid: {
                        display: true,
                        drawTicks: false
                    },
                    ticks: {
                        ...ChartUtils.ticks
                    }
                },
                y: {
                    type: 'linear',
                    beginAtZero: true,
                    grid: {
                        display: true,
                        color: ChartUtils.gridLineColor,
                        lineWidth: ctx => (ctx.tick.value === 0 ? 1 : 0),
                        borderDash: [6, 6]
                    },
                    ticks: {
                        ...ChartUtils.ticks,
                        callback: value => {
                            if (typeof value === 'number') {
                                return value.toLocaleString(this.locale()) + ` ${this.valueUnit()}`;
                            }
                            return value;
                        },
                        color: context => {
                            if (typeof context.tick.value === 'number' && context.tick.value < 0) {
                                return ChartUtils.ticksGreenColor;
                            }
                            return ChartUtils.ticksColor;
                        }
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: ChartUtils.labels
                },
                tooltip: {
                    ...ChartUtils.tooltip,
                    callbacks: {
                        title: tooltipItems => tooltipItems[0].dataset.label || '',
                        label: () => '',
                        footer: tooltipItems => {
                            const label = tooltipItems[0].label;
                            const value = tooltipItems[0].raw as number;
                            return `${this.tooltipValueLabel()}: ${value.toLocaleString(
                                this.locale()
                            )} ${this.valueUnit()}\n${this.tooltipCategoryLabel()}: ${label}`;
                        }
                    }
                }
            }
        };
    });
}
